// src/components/video/lessonVideo.js
//
// Draws one frame of an animated lesson video: a classroom, the lesson's
// scene (the teaching clock, big words, or a picture for the line), the SBL
// students acting it out, and the lyrics along the bottom lighting up word by
// word.
//
// It is driven by two things:
//
//   1. The song's timings, exactly as the Sing-along studio / auto-sync
//      saves them in lyricSync/{slug}: `lines` (the lyric lines) and `cues`
//      ([{ i, t }] — line i starts at t seconds, one cue per time it is sung).
//
//   2. A STORYBOARD (see storyboards/tellingTime.js): plain data that says
//      what happens on each lyric line. Shots are matched to lines by their
//      words, not by time, so re-syncing a song never breaks its video.
//
// Pure canvas, no React or Firebase — the builder preview and the MP4
// export call the same renderLessonFrame() every frame.

import { CAST, drawCharacter } from "./characters";
import { HOUR_COLOR, MINUTE_COLOR, drawClock } from "./clockScene";

const INK = "#2a2130";
const FONT = '"Baloo 2", "Fredoka", "Arial Rounded MT Bold", "Trebuchet MS", sans-serif';
const TAU = Math.PI * 2;
const ease = (x) => x * x * (3 - 2 * x);
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const lerp = (a, b, k) => a + (b - a) * k;

/* ------------------------------------------------------------------ */
/* Lyrics helpers (kept local so this file has no Firebase imports)    */
/* ------------------------------------------------------------------ */

export const normLine = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[‘’`]/g, "'")
    .replace(/[^a-z0-9' ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const wordsOf = (s) => String(s || "").trim().split(/\s+/).filter(Boolean);
const isHeader = (s) => /^\s*[([][^)\]]{1,40}[)\]]\s*:?\s*$/.test(String(s || ""));

/** The section label ("Chorus", "Verse") in force at line i. */
const sectionAt = (lines, i) => {
  for (let k = i; k >= 0; k -= 1) {
    const m = String(lines[k] || "").match(/^\s*[([]\s*([^)\]]{1,40}?)\s*[)\]]/);
    if (m) return m[1].replace(/^\w/, (c) => c.toUpperCase());
  }
  return "";
};

/* ------------------------------------------------------------------ */
/* Timeline: storyboard + cues → blocks with start/end                 */
/* ------------------------------------------------------------------ */

/**
 * @returns {{ blocks: Array, firstCue: number, lastCue: number }}
 * Each block: { ...shotDef, start, end, cueStarts: [t...] }
 * Consecutive cues that match the same shot id merge into one block, so a
 * chorus sung twice in a row is one continuous scene.
 */
export const buildTimeline = ({ storyboard, lines, cues, duration }) => {
  const sorted = [...(cues || [])].sort((a, b) => a.t - b.t);
  const seen = {};
  const blocks = [];
  const songEnd = Number.isFinite(duration) && duration > 0 ? duration : (sorted.at(-1)?.t || 0) + 12;

  if (storyboard.intro) {
    blocks.push({ ...storyboard.intro, id: "intro", start: 0, end: sorted[0]?.t ?? songEnd, cueStarts: [] });
  }

  sorted.forEach((cue) => {
    const text = normLine(lines[cue.i]);
    if (!text || isHeader(lines[cue.i])) return;
    seen[text] = (seen[text] || 0) + 1;
    const occ = seen[text];
    let shot;
    if (storyboard.byLine) {
      // Made in the Storyboard editor: one scene per lyric LINE NUMBER, so
      // it always matches the song exactly. "keep" = the scene above goes on.
      const def = storyboard.byLine[cue.i] ?? storyboard.byLine[String(cue.i)];
      if (!def || def.keep) {
        blocks.at(-1)?.cueStarts.push(cue.t);
        return;
      }
      // identical scenes on consecutive lines run as one (no restart)
      shot = { ...def, id: def.id || JSON.stringify(def), line: lines[cue.i] };
    } else {
      shot = (storyboard.shots || []).find(
        (s) =>
          text.startsWith(normLine(s.line)) &&
          (!s.minOccurrence || occ >= s.minOccurrence) &&
          (!s.occurrence || occ === s.occurrence)
      );
    }
    if (!shot) return;
    const last = blocks.at(-1);
    if (last && last.id === shot.id) {
      last.cueStarts.push(cue.t);
      return;
    }
    if (last) last.end = cue.t;
    blocks.push({ ...shot, start: cue.t, end: songEnd, cueStarts: [cue.t] });
  });

  // the outro takes over a few seconds after the last line starts
  const lastCue = sorted.at(-1)?.t ?? 0;
  if (storyboard.outro) {
    const at = Math.min(songEnd, lastCue + (storyboard.outro.after ?? 5));
    const last = blocks.at(-1);
    if (last) last.end = at;
    blocks.push({ ...storyboard.outro, id: "outro", start: at, end: songEnd + 30, cueStarts: [] });
  }
  return { blocks, firstCue: sorted[0]?.t ?? 0, lastCue, cues: sorted };
};

const blockAt = (timeline, t) => {
  const { blocks } = timeline;
  for (let k = blocks.length - 1; k >= 0; k -= 1) if (t >= blocks[k].start) return k;
  return 0;
};

/* ------------------------------------------------------------------ */
/* Clock state over time                                               */
/* ------------------------------------------------------------------ */

const DEFAULT_CLOCK = { hour: 12, minute: 0 };

/** Total minutes past 12:00 (so tweens always run clockwise). */
const toMinutes = (c) => ((c.hour % 12) * 60 + c.minute) % 720;

const fromMinutes = (m) => {
  const mm = ((m % 720) + 720) % 720;
  return { hour: Math.floor(mm / 60), minute: mm % 60 };
};

/**
 * The clock time a block ends on: its own target, or — for a spin — exactly
 * where the hands had got to when the block ended. (Returning the start of
 * the spin here made the hands snap back after every chorus.)
 */
const clockEndOf = (block, from) => {
  if (block.clock?.spin) {
    const dur = Math.max(0, (block.end || block.start) - block.start);
    return fromMinutes(toMinutes(from) + (dur / block.clock.spin) * 60);
  }
  if (block.notchCount) return { hour: block.clock?.hour ?? from.hour, minute: 5 };
  if (block.clock) return { hour: block.clock.hour, minute: block.clock.minute };
  return from;
};

/**
 * Work that only depends on the timeline, done once instead of every frame:
 * where the clock starts in each block, and when each student's current
 * action began.
 */
const cacheOf = (timeline) => {
  if (timeline._cache) return timeline._cache;
  const clockFrom = [];
  let from = DEFAULT_CLOCK;
  timeline.blocks.forEach((b) => {
    clockFrom.push(from);
    from = clockEndOf(b, from);
  });
  const since = timeline.blocks.map(() => ({}));
  const keys = new Set();
  timeline.blocks.forEach((b) => Object.keys(b.cast || {}).forEach((k) => keys.add(k)));
  Object.keys(CAST).forEach((k) => keys.add(k));
  keys.forEach((key) => {
    let prevSig = null;
    let start = 0;
    timeline.blocks.forEach((b, k) => {
      const sig = JSON.stringify(b.cast?.[key] || null);
      if (sig !== prevSig) start = b.start;
      since[k][key] = start;
      prevSig = sig;
    });
  });
  // Does the block before carry the same readout / label, so it can stay
  // on screen instead of fading out and back in?
  const keepReadout = timeline.blocks.map((b, k) => Boolean(b.readout && timeline.blocks[k - 1]?.readout));
  const keepLabels = timeline.blocks.map((b, k) => {
    const prev = new Set((timeline.blocks[k - 1]?.labels || []).map((l) => l.text));
    return (b.labels || []).map((l) => prev.has(l.text));
  });
  timeline._cache = { clockFrom, since, keepReadout, keepLabels, fit: new Map() };
  return timeline._cache;
};

const clockAt = (timeline, k, t) => {
  const from = cacheOf(timeline).clockFrom[k] || DEFAULT_CLOCK;
  const b = timeline.blocks[k];
  const local = t - b.start;
  const fromM = toMinutes(from);

  if (b.clock?.spin) {
    // minute hand sweeps round; one full turn every `spin` seconds
    const m = fromM + (local / b.clock.spin) * 60;
    return { hour: Math.floor(m / 60) % 12, minute: m % 60 };
  }
  if (b.notchCount) {
    // tick 1, 2, 3, 4, 5 minutes past the hour across the line
    const span = Math.min(5, Math.max(2, b.end - b.start - 0.4));
    const k5 = Math.min(5, 1 + Math.floor((local / span) * 5));
    return { hour: b.clock?.hour ?? from.hour, minute: k5, notches: k5 };
  }
  if (!b.clock) return from;
  // Forward to the new time, the way a clock moves — unless that is more
  // than six hours (e.g. coming out of a chorus spin), when going back the
  // short way is calmer than a twelve-hour blur.
  let delta = (((toMinutes(b.clock) - fromM) % 720) + 720) % 720;
  if (delta > 360) delta -= 720;
  if (delta === 0) return { hour: b.clock.hour, minute: b.clock.minute };
  const k2 = ease(clamp01(local / 1.0));
  const m = fromM + delta * k2;
  return { ...fromMinutes(m), arriving: k2 < 1 };
};

/* ------------------------------------------------------------------ */
/* Backdrop                                                            */
/* ------------------------------------------------------------------ */

const drawRoom = (ctx, W, H, t) => {
  const floorY = H * 0.8;
  const wall = ctx.createLinearGradient(0, 0, 0, floorY);
  wall.addColorStop(0, "#dbeeff");
  wall.addColorStop(1, "#c6ddf9");
  ctx.fillStyle = wall;
  ctx.fillRect(0, 0, W, H);
  // soft wall shapes
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  [
    [W * 0.08, H * 0.2, H * 0.12],
    [W * 0.92, H * 0.16, H * 0.09],
    [W * 0.85, H * 0.55, H * 0.06],
    [W * 0.12, H * 0.58, H * 0.07],
  ].forEach(([x, y, r], i) => {
    ctx.beginPath();
    ctx.arc(x, y + Math.sin(t * 0.6 + i) * 4, r, 0, TAU);
    ctx.fill();
  });
  // floor
  ctx.fillStyle = "#ecd0a4";
  ctx.fillRect(0, floorY, W, H - floorY);
  ctx.fillStyle = "#d9b886";
  for (let x = 0; x < W; x += W / 10) ctx.fillRect(x, floorY, 3, H - floorY);
  ctx.fillStyle = "#c9a46f";
  ctx.fillRect(0, floorY - 6, W, 9);
};

/* ------------------------------------------------------------------ */
/* Labels, readout, banners                                            */
/* ------------------------------------------------------------------ */

const pill = (ctx, x, y, text, size, bg, fg, alpha = 1) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `800 ${size}px ${FONT}`;
  const w = ctx.measureText(text).width + size * 1.1;
  const h = size * 1.55;
  ctx.beginPath();
  ctx.roundRect(x - w / 2, y - h / 2, w, h, h / 2);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, y + size * 0.06);
  ctx.restore();
  return { w, h };
};

const arrow = (ctx, x0, y0, x1, y1, color, alpha) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 6;
  ctx.lineCap = "round";
  const mx = (x0 + x1) / 2;
  const my = Math.min(y0, y1) - 30;
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.quadraticCurveTo(mx, my, x1, y1);
  ctx.stroke();
  const a = Math.atan2(y1 - my, x1 - mx);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1 - Math.cos(a - 0.45) * 20, y1 - Math.sin(a - 0.45) * 20);
  ctx.lineTo(x1 - Math.cos(a + 0.45) * 20, y1 - Math.sin(a + 0.45) * 20);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

/** "4:05" with the hour part orange and the minutes blue. */
const drawReadout = (ctx, x, y, clock, readout, alpha) => {
  if (!readout || alpha <= 0) return;
  const h = Math.round(clock.hour) % 12 || 12;
  const m = Math.round(clock.minute) % 60;
  const hText = String(h);
  const mText = `:${String(m).padStart(2, "0")}`;
  const size = 50;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `800 ${size}px ${FONT}`;
  const wH = ctx.measureText(hText).width;
  const wM = ctx.measureText(mText).width;
  const mins = Math.round(clock.minute) % 60;
  const sub =
    typeof readout.sub === "function"
      ? readout.sub(clock)
      : String(readout.sub || "").replace("{minutes}", `${mins} minute${mins === 1 ? "" : "s"}`);
  ctx.font = `700 26px ${FONT}`;
  const wS = sub ? ctx.measureText(sub).width + 24 : 0;
  const total = wH + wM + wS + 44;
  const bx = x - total / 2;
  ctx.beginPath();
  ctx.roundRect(bx, y - 38, total, 76, 22);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.font = `800 ${size}px ${FONT}`;
  const accent = readout.accent;
  ctx.fillStyle = accent === "minute" ? INK : HOUR_COLOR;
  ctx.fillText(hText, bx + 22, y + 3);
  ctx.fillStyle = accent === "hour" ? INK : MINUTE_COLOR;
  ctx.fillText(mText, bx + 22 + wH, y + 3);
  if (sub) {
    ctx.font = `700 26px ${FONT}`;
    ctx.fillStyle = "#5a5170";
    ctx.fillText(sub, bx + 22 + wH + wM + 24, y + 4);
  }
  ctx.restore();
};

/* ------------------------------------------------------------------ */
/* Lyrics along the bottom                                             */
/* ------------------------------------------------------------------ */

const drawLyricBar = (ctx, W, H, timeline, lines, t) => {
  const cues = timeline.cues;
  let k = -1;
  for (let j = 0; j < cues.length; j += 1) if (cues[j].t <= t) k = j;
  const barH = H * 0.14;
  const y0 = H - barH;
  ctx.save();
  ctx.fillStyle = "rgba(24,18,44,0.82)";
  ctx.fillRect(0, y0, W, barH);
  ctx.fillStyle = "#ffd23f";
  ctx.fillRect(0, y0, W, 4);

  const cue = cues[k];
  const next = cues[k + 1];
  // before the first line: the first line, waiting
  const showCue = cue || cues[0];
  if (!showCue) {
    ctx.restore();
    return;
  }
  const text = String(lines[showCue.i] || "");
  const words = wordsOf(text);
  // how far through the line we are, by a reading-speed estimate
  let lit = 0;
  if (cue) {
    const est = Math.min(7, Math.max(1.2, words.length * 0.42 + 0.4));
    const dur = next ? Math.min(est, Math.max(0.4, next.t - cue.t - 0.1)) : est;
    const frac = clamp01((t - cue.t) / dur);
    const weights = words.map((w) => w.length + 1);
    const total = weights.reduce((a, b) => a + b, 0);
    let acc = 0;
    words.forEach((w, idx) => {
      if (frac * total >= acc - 0.001) lit = idx + 1;
      acc += weights[idx];
    });
    const lineEnd = cue.t + dur + 2.2;
    if (!next && t > lineEnd) {
      ctx.restore();
      return; // song's last line has finished
    }
  }

  // section pill on the left
  const sec = sectionAt(lines, showCue.i);
  let left = 24;
  if (sec) {
    ctx.font = `800 20px ${FONT}`;
    const w = ctx.measureText(sec).width + 26;
    ctx.beginPath();
    ctx.roundRect(left, y0 + barH / 2 - 17, w, 34, 17);
    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(sec, left + w / 2, y0 + barH / 2 + 1);
    left += w + 16;
  }

  // the line itself, centred in the remaining space, shrunk to fit
  const room = W - left - 40;
  const fitKey = `${text}|${room}|${barH}`;
  const fit = cacheOf(timeline).fit;
  let size = fit.get(fitKey);
  if (!size) {
    size = Math.round(barH * 0.4);
    ctx.font = `800 ${size}px ${FONT}`;
    while (ctx.measureText(text).width > room && size > 20) {
      size -= 2;
      ctx.font = `800 ${size}px ${FONT}`;
    }
    fit.set(fitKey, size);
  }
  ctx.font = `800 ${size}px ${FONT}`;
  const space = ctx.measureText(" ").width;
  const widths = words.map((w) => ctx.measureText(w).width);
  const lineW = widths.reduce((a, b) => a + b, 0) + space * Math.max(0, words.length - 1);
  let x = Math.max(left, (W - lineW) / 2);
  const y = y0 + barH / 2 + 2;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  words.forEach((w, idx) => {
    ctx.lineWidth = 5;
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.strokeText(w, x, y);
    ctx.fillStyle = cue && idx < lit ? "#ffd23f" : "#ffffff";
    ctx.fillText(w, x, y);
    x += widths[idx] + space;
  });
  ctx.restore();
};

/* ------------------------------------------------------------------ */
/* Students                                                            */
/* ------------------------------------------------------------------ */

const SLOTS = {
  // fraction of width, and which way "toward the clock" is
  marcus: { x: 0.11, toward: "right", lane: 1 },
  emma: { x: 0.26, toward: "right", lane: 0 },
  lily: { x: 0.74, toward: "left", lane: 0 },
  noah: { x: 0.89, toward: "left", lane: 1 },
};

/** When this student's current action began (so it doesn't restart every line). */
const actionSince = (timeline, k, key) => {
  const cached = cacheOf(timeline).since[k]?.[key];
  if (cached !== undefined) return cached;
  const cur = timeline.blocks[k].cast?.[key];
  const sig = JSON.stringify(cur || null);
  let since = timeline.blocks[k].start;
  for (let j = k - 1; j >= 0; j -= 1) {
    if (JSON.stringify(timeline.blocks[j].cast?.[key] || null) !== sig) break;
    since = timeline.blocks[j].start;
  }
  return since;
};

const drawStudents = (ctx, W, H, timeline, k, t, logo) => {
  const b = timeline.blocks[k];
  const floorY = H * 0.8 + 6;
  const order = Object.keys(SLOTS)
    .filter((key) => CAST[key])
    .sort((a, c) => SLOTS[a].lane - SLOTS[c].lane);
  order.forEach((key, idx) => {
    const slot = SLOTS[key];
    const home = slot.x * W;
    const cast = b.cast?.[key] || { action: "idle" };
    let action = cast.action || "idle";
    let dir = cast.dir === "toward" || (action === "point" && !cast.dir) ? slot.toward : cast.dir;
    let x = home;
    let tt = t - actionSince(timeline, k, key);

    // intro: walk on from the nearest edge, then do the intro action
    if (b.walkIn) {
      const fromLeft = slot.toward === "right";
      const startX = fromLeft ? -90 : W + 90;
      const delay = idx * 0.35;
      const walkDur = 2.6;
      const local = t - b.start - delay;
      if (local < walkDur) {
        x = lerp(startX, home, clamp01(local / walkDur));
        action = "walk";
        dir = fromLeft ? "right" : "left";
        tt = Math.max(0, local);
      } else {
        tt = local - walkDur;
      }
    }
    const scale = (H / 720) * (0.74 + slot.lane * 0.03);
    drawCharacter(ctx, key, {
      x,
      y: floorY + slot.lane * 10 * (H / 720),
      scale,
      action,
      t: tt,
      dir,
      logo,
    });
  });
};

/** Big words in the middle of the screen (the "students only" scene). */
const drawCaption = (ctx, W, H, text, alpha) => {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  let size = Math.round(H * 0.095);
  const maxW = W * 0.62;
  const wrap = () => {
    ctx.font = `800 ${size}px ${FONT}`;
    const out = [];
    let line = "";
    String(text).split(/\s+/).forEach((w) => {
      const next = line ? `${line} ${w}` : w;
      if (ctx.measureText(next).width > maxW && line) {
        out.push(line);
        line = w;
      } else line = next;
    });
    if (line) out.push(line);
    return out;
  };
  let rows = wrap();
  while (rows.length > 3 && size > 30) {
    size -= 6;
    rows = wrap();
  }
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const lineH = size * 1.15;
  const y0 = H * 0.36 - ((rows.length - 1) * lineH) / 2;
  rows.forEach((row, i) => {
    ctx.lineWidth = 12;
    ctx.strokeStyle = "#ffffff";
    ctx.strokeText(row, W / 2, y0 + i * lineH);
    ctx.fillStyle = "#5a3fd0";
    ctx.fillText(row, W / 2, y0 + i * lineH);
  });
  ctx.restore();
};

/* ------------------------------------------------------------------ */
/* Pictures (the "picture" scene)                                      */
/* ------------------------------------------------------------------ */
//
// A line's scene can be a picture — the lyric pictures made by
// src/scripts/make-lyric-images.mjs, or any image URL. Drawing happens every
// frame and must be synchronous, so pictures load once into this cache and
// are simply skipped until they arrive (the studio redraws when one does).
//
// crossOrigin="anonymous" is not optional: a canvas that has drawn an image
// from another site without CORS permission can no longer be recorded. The
// bucket's CORS already lists the portal (the logo relies on the same thing).
// A picture that fails that check is never drawn, so it can't break a video.

const pictures = new Map(); // url -> { img, state: "loading" | "ok" | "bad" }
const pictureListeners = new Set();

/** Called with the url whenever a picture finishes loading (or fails). */
export const onPictureLoaded = (fn) => {
  pictureListeners.add(fn);
  return () => pictureListeners.delete(fn);
};

const loadPicture = (url) => {
  if (!url) return null;
  const hit = pictures.get(url);
  if (hit) return hit;
  const entry = { img: new Image(), state: "loading" };
  entry.img.crossOrigin = "anonymous";
  entry.img.decoding = "async";
  entry.promise = new Promise((resolve) => {
    entry.img.onload = () => {
      entry.state = "ok";
      pictureListeners.forEach((fn) => fn(url));
      resolve(true);
    };
    entry.img.onerror = () => {
      entry.state = "bad";
      pictureListeners.forEach((fn) => fn(url));
      resolve(false);
    };
  });
  entry.img.src = url;
  pictures.set(url, entry);
  return entry;
};

/** Every picture URL a timeline (or storyboard) uses. */
export const pictureUrlsOf = (timelineOrStoryboard) => {
  const out = new Set();
  const add = (shot) => shot?.picture?.url && out.add(shot.picture.url);
  if (timelineOrStoryboard?.blocks) timelineOrStoryboard.blocks.forEach(add);
  else {
    const sb = timelineOrStoryboard || {};
    add(sb.intro);
    add(sb.outro);
    Object.values(sb.byLine || {}).forEach(add);
  }
  return Array.from(out);
};

/**
 * Loads every picture before a recording starts, so the first frames of each
 * scene aren't blank. Resolves { ok, failed: [url] } — failed means the
 * picture couldn't be loaded with CORS and will be left out of the video.
 */
export const preloadPictures = async (urls, timeoutMs = 20000) => {
  const list = (urls || []).filter(Boolean);
  const results = await Promise.all(
    list.map((u) =>
      Promise.race([loadPicture(u).promise, new Promise((r) => setTimeout(() => r(false), timeoutMs))])
    )
  );
  const failed = list.filter((u, n) => !results[n]);
  return { ok: failed.length === 0, failed };
};

/** The picture's frame for the "board" layout — a classroom screen. */
const boardRect = (W, H) => {
  const w = W * 0.6;
  const h = Math.min(H * 0.62, (w * 9) / 16);
  return { x: (W - w) / 2, y: H * 0.035, w, h };
};

/**
 * Draws an image to fill a box (cropping the overflow). `cam` is a zoom
 * number, or { z, fx, fy }: zoom and the point of the picture (0–1 across,
 * 0–1 down) the camera moves toward. The picture always covers the box.
 */
const drawCover = (ctx, img, x, y, w, h, cam = 1) => {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const c = typeof cam === "number" ? { z: cam, fx: 0.5, fy: 0.5 } : cam;
  const s = Math.max(w / iw, h / ih) * Math.max(1, c.z);
  const dw = iw * s;
  const dh = ih * s;
  let dx = x + w / 2 - c.fx * dw;
  let dy = y + h / 2 - c.fy * dh;
  dx = Math.min(x, Math.max(x + w - dw, dx));
  dy = Math.min(y, Math.max(y + h - dh, dy));
  ctx.drawImage(img, dx, dy, dw, dh);
};

/* Camera moves on a picture (picture.camera, picked in the storyboard):
 * one picture can zoom to the labels under a bar graph, then to its scale,
 * then back out — without making new pictures. */
const smooth = (x) => {
  const v = clamp01(x);
  return v * v * (3 - 2 * v);
};
const CAMERA_TARGETS = {
  zoomBottom: { z: 1.75, fx: 0.5, fy: 0.92 },
  zoomLeft: { z: 1.85, fx: 0.06, fy: 0.5 },
  zoomRight: { z: 1.85, fx: 0.94, fy: 0.5 },
  zoomTop: { z: 1.75, fx: 0.5, fy: 0.08 },
  zoomCenter: { z: 1.6, fx: 0.5, fy: 0.5 },
};
const cameraAt = (block, local) => {
  const cam = block?.picture?.camera || "";
  const span = Math.max(2, (block?.end || (block?.start || 0) + 6) - (block?.start || 0));
  if (cam === "still") return { z: 1, fx: 0.5, fy: 0.5 };
  const T = CAMERA_TARGETS[cam];
  if (T) {
    const p = smooth(local / Math.min(1.8, span * 0.6));
    return { z: 1 + (T.z - 1) * p, fx: 0.5 + (T.fx - 0.5) * p, fy: 0.5 + (T.fy - 0.5) * p };
  }
  if (cam === "panRight") return { z: 1.35, fx: 0.15 + 0.7 * clamp01(local / span), fy: 0.5 };
  // default: a gentle push-in, so a still picture doesn't look frozen
  return { z: 1 + 0.045 * clamp01(local / span), fx: 0.5, fy: 0.5 };
};
const mixCam = (a, b, m) => ({ z: a.z + (b.z - a.z) * m, fx: a.fx + (b.fx - a.fx) * m, fy: a.fy + (b.fy - a.fy) * m });

/**
 * The picture scene. Crossfades from the picture before it (if the scene
 * before was a picture too) so line changes dissolve instead of cutting.
 * The SAME picture on the next line (a colour group) doesn't dissolve: the
 * camera glides from where it was to the new move.
 */
const drawPictureScene = (ctx, W, H, timeline, k, t) => {
  const b = timeline.blocks[k];
  const local = t - b.start;
  const full = b.picture?.fit === "full";
  const r = full ? { x: 0, y: 0, w: W, h: H } : boardRect(W, H);
  const cur = loadPicture(b.picture?.url);
  const prevBlock = timeline.blocks[k - 1];
  const prevIsPic = prevBlock?.scene === "picture" && prevBlock.picture?.fit === b.picture?.fit;
  const samePic = prevIsPic && prevBlock.picture?.url && prevBlock.picture.url === b.picture?.url;
  const prev = prevIsPic && !samePic ? loadPicture(prevBlock.picture?.url) : null;
  const mix = clamp01(local / 0.6);
  const prevEnd = prevIsPic ? cameraAt(prevBlock, (prevBlock.end || b.start) - prevBlock.start) : null;
  let cam = cameraAt(b, local);
  if (samePic) cam = mixCam(prevEnd, cam, smooth(local / 1.2));

  ctx.save();
  if (!full) {
    // the screen: shadow, white bezel, rounded corners
    ctx.fillStyle = "rgba(42,33,48,0.18)";
    ctx.beginPath();
    ctx.roundRect(r.x + 6, r.y + 10, r.w, r.h, 26);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.roundRect(r.x - 10, r.y - 10, r.w + 20, r.h + 20, 30);
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = INK;
    ctx.stroke();
    ctx.beginPath();
    ctx.roundRect(r.x, r.y, r.w, r.h, 20);
    ctx.clip();
    ctx.fillStyle = "#eef4ff";
    ctx.fillRect(r.x, r.y, r.w, r.h);
  }
  if (prev?.state === "ok" && mix < 1) {
    drawCover(ctx, prev.img, r.x, r.y, r.w, r.h, prevEnd);
  }
  if (cur?.state === "ok") {
    ctx.globalAlpha = samePic ? 1 : prev?.state === "ok" ? mix : clamp01(local / 0.35);
    drawCover(ctx, cur.img, r.x, r.y, r.w, r.h, cam);
    ctx.globalAlpha = 1;
  } else if (!prev || prev.state !== "ok") {
    // still loading (or blocked): a soft placeholder rather than nothing
    ctx.fillStyle = "rgba(90,63,208,0.10)";
    ctx.fillRect(r.x, r.y, r.w, r.h);
  }
  ctx.restore();

  // exact words / numbers over the picture — AI pictures never draw these
  if (b.caption) {
    const a = clamp01((local - 0.2) / 0.4);
    const size = Math.round(H * (full ? 0.07 : 0.06));
    const y = full ? H * (b.banner ? 0.2 : 0.12) : r.y + r.h - size * 0.95;
    pill(ctx, W / 2, y, b.caption, size, "#ffffff", "#5a3fd0", a);
  }
};

/* ------------------------------------------------------------------ */
/* One frame                                                           */
/* ------------------------------------------------------------------ */

/**
 * opts: {
 *   W, H, t,
 *   timeline,        from buildTimeline()
 *   lines,           lyric lines (lyricSync.lines)
 *   title,           e.g. "Telling Time"
 *   logo,            SBL logo <img> (crossOrigin) — shirts + corner
 *   fade = 1,        1 visible … 0 black (the sing-along ending fade)
 * }
 */
export const renderLessonFrame = (ctx, opts) => {
  const { W, H, t, timeline, lines, title, logo = null, fade = 1 } = opts;
  const k = blockAt(timeline, t);
  const b = timeline.blocks[k];
  const local = t - b.start;
  const fx = b.fx || {};
  const fadeIn = clamp01(local / 0.5);

  drawRoom(ctx, W, H, t);

  const scene = b.scene || "clock";
  const showClock = scene === "clock";
  if (scene === "picture") drawPictureScene(ctx, W, H, timeline, k, t);
  else if (!showClock && b.caption) drawCaption(ctx, W, H, b.caption, fadeIn);

  // clock
  const cx = W / 2;
  const cy = H * 0.4;
  const r = H * 0.245;
  const clock = clockAt(timeline, k, t);
  const fiveSpan = Math.min(4.5, Math.max(1.5, b.end - b.start - 0.3));
  if (showClock) drawClock(ctx, {
    cx,
    cy,
    r,
    hour: clock.hour,
    minute: clock.minute,
    glowHour: (fx.glowHour || 0) * fadeIn,
    glowMinute: (fx.glowMinute || 0) * fadeIn,
    glowNumbers: fx.numbers || [],
    glowNumberAmt: clock.arriving ? 0.35 : fadeIn,
    glowNotches: clock.notches ? Array.from({ length: clock.notches }, (_, n) => n + 1) : [],
    ringHours: (fx.ringHours || 0) * fadeIn,
    ringMinutes: (fx.ringMinutes || 0) * fadeIn,
    fiveLabels: fx.fiveCount ? 12 * clamp01(local / fiveSpan) : fx.fiveLabels || 0,
    wobbleHour: fx.wobbleHour || 0,
    t,
  });

  // hand labels with arrows
  (showClock ? b.labels || [] : []).forEach((lab, n) => {
    const a = cacheOf(timeline).keepLabels[k]?.[n] ? 1 : clamp01((local - 0.15 - n * 0.6) / 0.4);
    if (a <= 0) return;
    const leftSide = lab.side ? lab.side === "left" : n % 2 === 0;
    const lx = cx + (leftSide ? -1 : 1) * r * 1.62;
    const ly = cy - r * 0.62 + n * 70;
    const color = lab.color === "minute" ? MINUTE_COLOR : HOUR_COLOR;
    const box = pill(ctx, lx, ly, lab.text, 28, color, "#ffffff", a);
    let tx = cx;
    let ty = cy;
    if (lab.target === "hourHand" || lab.target === "minuteHand") {
      const isH = lab.target === "hourHand";
      const frac = isH ? ((clock.hour % 12) + clock.minute / 60) / 12 : clock.minute / 60;
      const ang = frac * TAU - Math.PI / 2;
      const len = r * (isH ? 0.36 : 0.62);
      tx = cx + Math.cos(ang) * len;
      ty = cy + Math.sin(ang) * len;
    } else if (lab.target === "numbers") {
      const ang = (leftSide ? 9 / 12 : 3 / 12) * TAU - Math.PI / 2 - 0.35;
      tx = cx + Math.cos(ang) * r * 0.72;
      ty = cy + Math.sin(ang) * r * 0.72;
    } else if (lab.target === "notches") {
      const ang = (leftSide ? 10 / 12 : 2 / 12) * TAU - Math.PI / 2 + 0.2;
      tx = cx + Math.cos(ang) * r * 0.93;
      ty = cy + Math.sin(ang) * r * 0.93;
    }
    arrow(ctx, lx + (leftSide ? box.w / 2 - 10 : -box.w / 2 + 10), ly + 8, tx, ty, color, a);
  });

  // digital readout under the clock
  // show the time the hands are heading to, not the in-between frames
  const shownTime =
    b.clock && !b.clock.spin && !b.notchCount ? { hour: b.clock.hour, minute: b.clock.minute } : clock;
  const keepReadout = cacheOf(timeline).keepReadout[k];
  drawReadout(ctx, cx, cy + r * 1.6, shownTime, showClock ? b.readout : null,
    keepReadout ? 1 : clamp01((local - 0.6) / 0.4));

  drawStudents(ctx, W, H, timeline, k, t, logo);

  // title card at the start
  if (b.id === "intro" && title) {
    const a = clamp01(local / 0.6) * (1 - clamp01((local - 4.2) / 0.6));
    if (a > 0) {
      ctx.save();
      ctx.globalAlpha = a;
      ctx.font = `800 ${Math.round(H * 0.068)}px ${FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const y = H * 0.062 + (1 - a) * -20;
      ctx.lineWidth = 10;
      ctx.strokeStyle = "#ffffff";
      ctx.strokeText(title, W / 2, y);
      ctx.fillStyle = "#5a3fd0";
      ctx.fillText(title, W / 2, y);
      ctx.restore();
    }
  }
  if (b.banner) {
    const a = clamp01(local / 0.5);
    pill(ctx, W / 2, H * 0.08, b.banner, Math.round(H * 0.045), "#ffd23f", INK, a);
  }

  drawLyricBar(ctx, W, H, timeline, lines, t);

  // corner logo
  if (logo && logo.naturalWidth) {
    const lw = W * 0.07;
    const lh = (lw * logo.naturalHeight) / logo.naturalWidth;
    ctx.save();
    ctx.globalAlpha = 0.9;
    ctx.drawImage(logo, W - lw - 18, 14, lw, lh);
    ctx.restore();
  }

  if (fade < 1) {
    ctx.save();
    ctx.globalAlpha = 1 - Math.max(0, fade);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }
};

export default renderLessonFrame;
